import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@apps/web/lib/utils';

function Dialog({
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
    return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
    return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
    return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
    return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
    className,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
    return (
        <DialogPrimitive.Overlay
            data-slot="dialog-overlay"
            className={cn(
                'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50',
                className,
            )}
            {...props}
        />
    );
}

function DialogContent({
    className,
    children,
    showCloseButton = true,
    phoneLayout = 'sheet',
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
    readonly showCloseButton?: boolean;
    // 'centered' keeps the desktop dialog on phones too.
    readonly phoneLayout?: 'sheet' | 'fullscreen' | 'centered';
}) {
    return (
        <DialogPortal data-slot="dialog-portal">
            <DialogOverlay />
            <DialogPrimitive.Content
                data-slot="dialog-content"
                data-phone-layout={phoneLayout}
                // Below `md` (the `useIsMobile` line) render a bottom sheet or
                // a full screen. The `!` sizes must beat callers' `sm:` sizes,
                // which Tailwind emits after `max-md:` rules.
                className={cn(
                    'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed z-50 grid grid-cols-1 border shadow-lg duration-200',
                    'md:data-[state=closed]:zoom-out-95 md:data-[state=open]:zoom-in-95 top-[50%] left-[50%] w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg p-6 sm:max-w-lg',
                    phoneLayout !== 'centered' &&
                        'max-md:data-[state=closed]:slide-out-to-bottom-4 max-md:data-[state=open]:slide-in-from-bottom-4 max-md:inset-x-0 max-md:bottom-0 max-md:left-0 max-md:flex max-md:max-w-none! max-md:translate-x-0 max-md:translate-y-0 max-md:flex-col max-md:overflow-y-auto',
                    phoneLayout === 'sheet' &&
                        'max-md:top-auto max-md:max-h-[90dvh] max-md:rounded-2xl max-md:rounded-b-none max-md:p-4 max-md:pb-[max(1rem,var(--safe-bottom))]',
                    phoneLayout === 'fullscreen' &&
                        'max-md:top-0 max-md:h-dvh! max-md:max-h-dvh! max-md:w-full! max-md:rounded-none max-md:border-0 max-md:[--dialog-padding:1rem] max-md:pt-[calc(var(--dialog-padding)+var(--safe-top))] max-md:pr-[max(var(--dialog-padding),var(--safe-right))] max-md:pb-[max(var(--dialog-padding),var(--safe-bottom))] max-md:pl-[max(var(--dialog-padding),var(--safe-left))]',
                    className,
                )}
                {...props}
            >
                {children}
                {showCloseButton && (
                    <DialogPrimitive.Close
                        data-slot="dialog-close"
                        className={cn(
                            "ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 -m-3.5 flex size-11 cursor-pointer items-center justify-center rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                            phoneLayout === 'fullscreen' &&
                                'max-md:top-[calc(1rem+var(--safe-top))] max-md:right-[calc(1rem+var(--safe-right))]',
                        )}
                    >
                        <X />
                        <span className="sr-only">Close</span>
                    </DialogPrimitive.Close>
                )}
            </DialogPrimitive.Content>
        </DialogPortal>
    );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="dialog-header"
            className={cn(
                'flex flex-col gap-2 text-center sm:text-left',
                className,
            )}
            {...props}
        />
    );
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="dialog-footer"
            className={cn(
                'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
                className,
            )}
            {...props}
        />
    );
}

function DialogTitle({
    className,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
    return (
        <DialogPrimitive.Title
            data-slot="dialog-title"
            className={cn('text-lg leading-none font-semibold', className)}
            {...props}
        />
    );
}

function DialogDescription({
    className,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
    return (
        <DialogPrimitive.Description
            data-slot="dialog-description"
            className={cn('text-muted-foreground text-sm', className)}
            {...props}
        />
    );
}

export {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
    DialogTrigger,
};
